import type { EmailSettings } from '@reflex/shared';
import { Badge } from '@reflex/ui/components/ui/badge';
import { SettingsRow } from '@reflex/ui/components/shared/SettingsRow';
import { PageDescription } from '@reflex/ui/components/layout/Typography';
import { AdminSubPageLayout } from '@reflex/ui/components/layout/AdminSubPageLayout';
import { AsyncContent } from '@/components/shared/AsyncContent';
import { useEmailSettings } from '@/hooks/config/useEmailSettingsAdmin';

const DESCRIPTION =
  'How transactional email (invites, org welcome, trial notices) is configured for this deployment. Email is configured through environment variables, so this view is read-only.';

/** Why email is off, phrased for the specific reason. Null when it is enabled. */
function disabledReason(settings: EmailSettings): string | null {
  if (settings.enabled) return null;
  if (settings.sendingDisabled) {
    return 'Sending is turned off by the EMAIL_SENDING_DISABLED kill switch, even though a key may be configured.';
  }
  return 'Set CUSTOMERIO_API_KEY and EMAIL_FROM to enable sending. This is the expected state for self-hosted / VPC deployments.';
}

export function AdminEmailSettingsPage() {
  const settingsQuery = useEmailSettings();

  return (
    <AdminSubPageLayout title="Email" description={DESCRIPTION}>
      <AsyncContent
        isLoading={settingsQuery.isLoading}
        isError={settingsQuery.isError}
        loadingMessage="Loading email settings…"
        errorMessage="Failed to load email settings."
      >
        {settingsQuery.data && <EmailSettingsView settings={settingsQuery.data} />}
      </AsyncContent>
    </AdminSubPageLayout>
  );
}

function EmailSettingsView({ settings }: { settings: EmailSettings }) {
  const reason = disabledReason(settings);

  return (
    <div className="space-y-4">
      <SettingsRow
        title="Email sending"
        description="Whether Reflex will actually deliver transactional email."
        control={
          <Badge variant={settings.enabled ? 'default' : 'secondary'}>
            {settings.enabled ? 'Enabled' : 'Disabled'}
          </Badge>
        }
      />

      {reason && (
        <PageDescription className="mt-0 text-xs text-muted-foreground">{reason}</PageDescription>
      )}

      <SettingsRow
        title="Provider"
        description="Delivery transport handling outbound email."
        control={
          <Badge variant={settings.transport ? 'outline' : 'subtle'}>
            {settings.transport ?? 'None'}
          </Badge>
        }
      />

      <SettingsRow
        title="Sender address"
        description="The from-address on outbound email (EMAIL_FROM)."
        control={
          <Badge variant={settings.fromAddress ? 'outline' : 'subtle'}>
            {settings.fromAddress ?? 'Not set'}
          </Badge>
        }
      />

      <SettingsRow
        title="Region"
        description="Customer.io data-residency region (CUSTOMERIO_REGION)."
        control={<Badge variant="outline">{settings.region.toUpperCase()}</Badge>}
      />

      <SettingsRow
        title="API key"
        description="Whether a Customer.io API key is configured. The key itself is never shown."
        control={
          <Badge variant={settings.apiKeyConfigured ? 'default' : 'subtle'}>
            {settings.apiKeyConfigured ? 'Configured' : 'Not set'}
          </Badge>
        }
      />

      <SettingsRow
        title="Kill switch"
        description="EMAIL_SENDING_DISABLED forces email off even when a key is present."
        control={
          <Badge variant={settings.sendingDisabled ? 'destructive' : 'subtle'}>
            {settings.sendingDisabled ? 'On' : 'Off'}
          </Badge>
        }
      />

      <SettingsRow
        title="Templates"
        description="Transactional emails this deployment can send."
        control={
          settings.templates.length > 0 ? (
            <div className="flex flex-wrap justify-end gap-1">
              {settings.templates.map((template) => (
                <Badge key={template} variant="outline">
                  {template}
                </Badge>
              ))}
            </div>
          ) : (
            <Badge variant="subtle">None</Badge>
          )
        }
      />
    </div>
  );
}
